import React, { forwardRef, Ref } from 'react';
import { FormInput } from '@spotify-internal/encore-web';

import { FieldController } from 'client/shared/FieldController';
import { FieldInputProps, ControlledFieldInputProps } from './types';
import { FieldBottomSection } from '../FieldBottomSection';

/**
 * TODO https://anchorfm.atlassian.net/browse/WHEEL-864: Add support for icons using FormInputIcon
 * https://encore.spotify.net/web/components/form?format=web-js#forminputicon
 *
 * TODO https://anchorfm.atlassian.net/browse/WHEEL-865: Add support for showing a clear button (likely by using type="search"
 * which should automatically show one)
 *
 * Both of the above are currently only required by the "plays by podcast" chart
 * on the Network screen
 * (see client/screens/NetworkScreen/components/PlaysByPodcastChart/index.tsx)
 */
export const FieldInput = forwardRef<HTMLElement, FieldInputProps>(
  (
    {
      name,
      type,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      value,
      min,
      max,
      autoFocus,
      maxCharacterLength,
      error,
      isShowingError = !!error,
      showCharacterCount,
      disabled,
      containerClassName,
      'aria-describedby': ariaDescribedBy = `${name}Error`,
      ...props
    }: FieldInputProps,
    ref: Ref<HTMLElement>
  ) => {
    /**
     * Since most of our FieldInput properties are just pass-throughs to
     * encore's FormInput, we could potentially just pass along all props to
     * FormInput below (and save us from doing things like onChange={onChange}).
     * However, should someone add properties to FieldInput that FormInput
     * should NOT receive, we might run into issues. Right now they are
     * deconstructed away above. So we play it safe.
     */
    return (
      <div className={containerClassName}>
        <FormInput
          {...props}
          ref={ref}
          name={name}
          error={isShowingError}
          id={name}
          data-cy={name}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          value={value}
          type={type}
          disabled={disabled}
          min={min}
          max={max}
          maxLength={maxCharacterLength}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          aria-describedby={ariaDescribedBy}
        />
        <FieldBottomSection
          errorContainerId={ariaDescribedBy}
          isShowingError={isShowingError}
          showCharacterCount={showCharacterCount}
          maxCharacterLength={maxCharacterLength}
          value={value}
          error={error}
        />
      </div>
    );
  }
);

export const ControlledFieldInput = (props: ControlledFieldInputProps) => (
  <FieldController<FieldInputProps, HTMLInputElement>
    as={FieldInput}
    {...props}
  />
);
